import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ResponseStatuses } from '@src/utils/enums';
import { HELP_QUERY_KEYS, HelpQueries } from './query';
import { HelpMutations } from './mutation';
import {
  GetHelpStatus,
  SubmitHelpPayload,
  UseHelpStatusQueryOptions,
  UseSubmitHelpMutationOptions,
  UseHelpInquirySummaryQueryOptions,
  UpdateHelpInquiryStatusParams,
  UseUpdateHelpInquiryStatusOptions,
  UseCancelHelpInquiryStatusOptions,
} from './types';
import { HelpInquiryRouteParams } from './config';
import { queryClient } from '@src/api/queryClient';
import { DASHBOARD_QUERY_KEYS } from '../dashboard';
import { toast } from 'react-toastify';

export const useHelpStatusQuery = ({
  enabled = true,
  requestId,
  version,
  helpInquiryStatus,
}: UseHelpStatusQueryOptions = {}) => {
  const hasRequestContext = requestId !== undefined && requestId !== null && version !== undefined && version !== null;

  return useQuery({
    queryKey: [HELP_QUERY_KEYS.GET_HELP_STATUS, requestId, version, helpInquiryStatus],
    queryFn: () => HelpQueries.getHelpStatus(),
    enabled: enabled && hasRequestContext,
  });
};

export const useSubmitHelpMutation = ({ requestId, version, onSuccess }: UseSubmitHelpMutationOptions = {}) => {
  const queryClient = useQueryClient();

  const helpStatusQueryKey = [HELP_QUERY_KEYS.GET_HELP_STATUS, requestId, version];

  const updateHelpStatusQuery = (canRequestAssistance: boolean) => {
    if (requestId === undefined || requestId === null || version === undefined || version === null) {
      return;
    }

    queryClient.setQueryData<GetHelpStatus>(helpStatusQueryKey, {
      canRequestAssistance,
    });

    void queryClient.invalidateQueries({
      queryKey: helpStatusQueryKey,
    });
  };

  return useMutation({
    mutationFn: (payload: SubmitHelpPayload) => HelpMutations.submitHelp(payload),
    onSuccess: (response) => {
      if (response.status === ResponseStatuses.Success || response.status === ResponseStatuses.AlreadyExist) {
        updateHelpStatusQuery(false);
      }

      onSuccess?.(response);
    },
  });
};

export const useHelpInquirySummaryQuery = ({
  requestId,
  version,
  helpInquiryId,
  enabled = true,
}: UseHelpInquirySummaryQueryOptions) =>
  useQuery({
    queryKey: [HELP_QUERY_KEYS.GET_HELP_INQUIRY_SUMMARY, requestId, version, helpInquiryId],
    queryFn: () =>
      HelpQueries.getHelpInquirySummary({
        requestId,
        version,
        helpInquiryId,
      }),
    enabled,
  });

export const useUpdateHelpInquiryStatusMutation = ({ onSuccess, onError }: UseUpdateHelpInquiryStatusOptions = {}) => {
  return useMutation({
    mutationFn: (payload: UpdateHelpInquiryStatusParams) => HelpMutations.updateHelpInquiryStatus(payload),
    onSuccess: (response) => {
      onSuccess?.(response);
    },
    onError: () => {
      onError?.();
    },
  });
};

export const useCancelHelpInquiryStatusMutation = ({ onSuccess, onError }: UseCancelHelpInquiryStatusOptions = {}) => {
  return useMutation({
    mutationFn: (payload: HelpInquiryRouteParams) => HelpMutations.cancelHelpInquiry(payload),
    onSuccess: (response) => {
      toast.success(response.msg);
      queryClient.invalidateQueries({ queryKey: [DASHBOARD_QUERY_KEYS.GET_DASHBOARD] });
      onSuccess?.(response);
    },
    onError: () => {
      console.error('Failed to cancel help inquiry.');
      onError?.();
    },
  });
};
